import {css} from '@mui/material';
import {LoadingSpinner, NoDataMessageWithIcon} from '@wallaby-ui/core';
import * as Plotly from 'plotly.js';
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {IntlShape, useIntl} from 'react-intl';
import createPlotlyComponent from 'react-plotly.js/factory';

import {
  AlertBoardChartDto,
  AlertBoardChartSeriesDto,
  AlertBoardChartType,
  AlertBoardChartValues,
  DEFAULT_ALERT_BOARD_CHART_TYPE,
  DEFAULT_ALERT_BOARD_CHART_VALUES,
} from './alert_board_chart_types';
import {ALERT_DISPLAYED_STATE_ENUM, AlertEpisodeDisplayedState} from './alert_types';
import {CHART_MONTH_TICKFORMAT, ChartColor} from '../LightChart';
import {LightChartLegend2} from '../LightChartLegend2';
import {useFormatters} from '../LightFormatter';

const Plot = createPlotlyComponent(Plotly);

/**
 * The reports' grey of their Other series (the last of the department report's chart colours), its area half opaque as
 * theirs. Inactive and Other take it, as the reports' charts beside this one (PDW-404).
 */
const REPORT_OTHER_COLOR = {line: '#878D96', fill: 'rgba(135,141,150,0.50)'};
/** The design's colours of each state's line and stacked area. Plotly takes no CSS variables. */
const STATE_COLORS: Record<AlertEpisodeDisplayedState, {line: string; fill: string}> = {
  ACTIVE: {line: '#EE828C', fill: 'rgba(240,150,158,0.45)'},
  SNOOZED: {line: '#F1C34D', fill: 'rgba(255,224,138,0.50)'},
  SOLVED: {line: '#71CE92', fill: 'rgba(150,221,170,0.50)'},
  INACTIVE: REPORT_OTHER_COLOR,
  DELETED: {line: '#C4CBD1', fill: 'rgba(196,203,209,0.55)'},
};
/** The design's five alert colours, and a sixth for six alerts charted alone, which the design never shows. */
const ALERT_COLORS = ['#F06595', '#B197FC', '#FFD43B', '#3BC9DB', '#69DB7C', '#4DABF7'];
const OTHER_COLOR = REPORT_OTHER_COLOR;

// the legend is the app's own above the plot, as on the reports' charts, so the plot keeps only a little room on top
const MARGIN = {l: 44, r: 16, t: 10, b: 34};
/** The traces' marker size, and the dot of the app's legend (LightChartLegend2) the hover box's swatches take. */
const MARKER_SIZE = 6;
const LEGEND_DOT_SIZE = 10;
/**
 * Below these Plotly has no room for its axes, so nothing is drawn. The height is the margins and a little plot: the
 * legend above takes its share of a low pane, and the pane's minimum must still leave a chart.
 */
const MIN_PLOT_WIDTH = 120;
const MIN_PLOT_HEIGHT = MARGIN.t + MARGIN.b + 40;

type Props = {
  chart: AlertBoardChartDto | undefined;
  values?: AlertBoardChartValues | null;
  chartType?: AlertBoardChartType | null;
  isLoading: boolean;
};

/** A series' key in the legend's hidden map, its label and its colours. */
type SeriesLook = {key: string; label: string; line: string; fill: string};

function seriesKey(series: AlertBoardChartSeriesDto): string {
  if (series.state) return `state:${series.state}`;
  return series.userFilterId == null ? 'other' : `alert:${series.userFilterId}`;
}

function alpha(hex: string): string {
  const h = hex.replace('#', '');
  return `rgba(${parseInt(h.slice(0, 2), 16)},${parseInt(h.slice(2, 4), 16)},${parseInt(h.slice(4, 6), 16)},0.45)`;
}

function escapeHtml(text: string): string {
  return text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

/** Plotly's decimal and thousands separators of the user's locale, as the tables format numbers. */
function separators(intl: IntlShape): string {
  const parts = new Intl.NumberFormat(intl.locale).formatToParts(12345.6);
  const decimal = parts.find((v) => v.type === 'decimal')?.value ?? '.';
  const group = parts.find((v) => v.type === 'group')?.value ?? ',';
  return `${decimal}${group}`;
}

/**
 * The board's dynamics chart (PDW-249): the episodes by the month they first became active, stacked areas with line and
 * markers or plain lines, as the design's Plotly chart, under the app's chart legend of the reports (a click hides a
 * series, a double click keeps it alone). It fills its pane.
 */
export function AlertBoardChart(props: Props): React.ReactElement {
  const {chart, isLoading} = props;
  const values = props.values ?? DEFAULT_ALERT_BOARD_CHART_VALUES;
  const chartType = props.chartType ?? DEFAULT_ALERT_BOARD_CHART_TYPE;
  const intl = useIntl();
  const {t, tEnum} = useFormatters();

  const plotAreaRef = useRef<HTMLDivElement>(null);
  // the reports' legend keeps the hidden series in a ref and asks for a render after each click
  const hiddenMap = useRef<Record<string | number, boolean>>({});
  const [legendRevision, setLegendRevision] = useState(0);
  const onSetHiddenMap = useCallback(() => setLegendRevision((v) => v + 1), []);
  const [size, setSize] = useState({width: 0, height: 0});
  useEffect(() => {
    const el = plotAreaRef.current;
    if (!el) return undefined;
    const measure = () => {
      const rect = el.getBoundingClientRect();
      const width = Math.floor(rect.width);
      const height = Math.floor(rect.height);
      setSize((prev) => (prev.width === width && prev.height === height ? prev : {width, height}));
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const looks: SeriesLook[] = useMemo(() => {
    let alertIndex = 0;
    return (chart?.series ?? []).map((series: AlertBoardChartSeriesDto) => {
      const key = seriesKey(series);
      if (series.state) return {key, label: tEnum(ALERT_DISPLAYED_STATE_ENUM, series.state), ...STATE_COLORS[series.state]};
      if (series.userFilterId == null) return {key, label: t('department.report.category.other'), ...OTHER_COLOR};
      const color = ALERT_COLORS[alertIndex % ALERT_COLORS.length];
      alertIndex += 1;
      return {key, label: series.name ?? '', line: color, fill: alpha(color)};
    });
  }, [chart, t, tEnum]);

  const plot = useMemo(() => {
    const x = chart?.months ?? [];
    const stacked = chartType === 'STACKED_LINE_CHART';
    const data = (chart?.series ?? []).map((series, index) => {
      const look = looks[index];
      return {
        x,
        y: series.points.map((point) => (values === 'PER_1000_CARE_DAYS' ? point.countPer1000CareDays ?? 0 : point.count)),
        name: look.label,
        visible: !hiddenMap.current[look.key],
        type: 'scatter',
        mode: 'lines+markers',
        line: {color: look.line, width: 2, shape: 'linear'},
        marker: {color: look.line, size: MARKER_SIZE},
        fill: stacked ? 'tonexty' : 'none',
        fillcolor: stacked ? look.fill : undefined,
        stackgroup: stacked ? 'one' : undefined,
        hovertemplate: `%{y} ${escapeHtml(look.label)}<extra></extra>`,
      };
    });
    const layout = {
      margin: MARGIN,
      width: size.width,
      height: size.height,
      autosize: false,
      paper_bgcolor: '#fff',
      plot_bgcolor: '#fff',
      separators: separators(intl),
      // Plotly's own font and grid, as the reports' charts (LightChart) keep them (PDW-404)
      xaxis: {
        // the months as the reports' time axes show them
        type: 'date',
        tickformat: CHART_MONTH_TICKFORMAT,
        hoverformat: CHART_MONTH_TICKFORMAT,
        // the last month's label is centred on the plot's edge, so the design's right margin grows when it is wider
        automargin: true,
        showgrid: false,
        showline: false,
        zeroline: false,
        fixedrange: true,
      },
      yaxis: {
        zeroline: false,
        rangemode: 'tozero',
        fixedrange: true,
      },
      hovermode: 'x unified',
      showlegend: false,
    };
    return {data, layout};
    // hiddenMap is a ref the legend changes in place: its click bumps the revision, so the traces read it again
  }, [chart, looks, values, chartType, intl, size, legendRevision]); // eslint-disable-line react-hooks/exhaustive-deps

  const legendItems: ChartColor[] = looks.map((look) => ({
    id: look.key,
    label: look.label,
    color: look.line,
    hidden: Boolean(hiddenMap.current[look.key]),
  }));

  const isEmpty = Boolean(chart) && !chart?.months.length;
  const canDraw = Boolean(chart?.months.length) && size.width >= MIN_PLOT_WIDTH && size.height >= MIN_PLOT_HEIGHT;
  return (
    <div css={styles.wrapper} data-name={'dl-alert-board-chart'}>
      {legendItems.length > 0 && (
        <div css={styles.legend}>
          <LightChartLegend2
            items={legendItems}
            hiddenMap={hiddenMap}
            onSetHiddenMap={onSetHiddenMap}
            legendRowStyles={{marginLeft: 'auto'}}
            textColor={'black'}
          />
        </div>
      )}
      <div css={styles.plotArea} ref={plotAreaRef}>
        {canDraw && (
          <Plot
            data={plot.data as Plotly.Data[]}
            layout={plot.layout as Partial<Plotly.Layout>}
            // no Plotly tip ("Double-click on legend to isolate one trace") on the first legend click, as LightChart
            config={{displayModeBar: false, responsive: false, showTips: false}}
          />
        )}
        {isEmpty && !isLoading && (
          <div css={styles.empty}>
            <NoDataMessageWithIcon message={'chart.emptyData'} />
          </div>
        )}
        {isLoading && <LoadingSpinner isVisible text='' />}
      </div>
    </div>
  );
}

const styles = {
  wrapper: css`
    display: flex;
    flex-direction: column;
    flex: 1 1 0;
    min-height: 0;
    width: 100%;
  `,
  /* the reports' legend row, right-aligned above the plot */
  legend: css`
    display: flex;
    flex: 0 0 auto;
    width: 100%;
    margin-bottom: 4px;
  `,
  plotArea: css`
    position: relative;
    flex: 1 1 0;
    min-height: 0;
    width: 100%;
    overflow: hidden;
    /*
     * The unified hover box draws each series' swatch as Plotly's legend: the area under the line and the trace's small
     * marker. The app's legend above the plot shows the line with a bigger dot alone, so the box does the same: no area,
     * the dot scaled up around the swatch's centre (Plotly puts it at 20px, halfway along the 30px line from 5px).
     */
    .hoverlayer .legend .legendfill {
      display: none;
    }
    .hoverlayer .legend path.scatterpts {
      transform: translate(20px, 0) scale(${LEGEND_DOT_SIZE / MARKER_SIZE});
    }
  `,
  empty: css`
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
  `,
};
